import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import { fetchData } from "../../fetchData";

const colorSwitch = (num: number) => {
  switch (num) {
    case 2:
      return "#FF8352";
    case 3:
      return "#FFAA4D";
    case 4:
      return "#AFEF41";
    case 5:
      return "#45E330";
    default:
      return "#FC495B";
  }
};

const reasonMessage = (reason: string) => {
  const map: Record<string, string> = {
    missing_params:
      "Este link está incompleto. Use o link enviado pela Atlântico.",
    invalid_params: "Este link é inválido. Solicite um novo link à Atlântico.",
    survey_not_found: "Pesquisa não encontrada.",
    survey_closed: "Esta pesquisa já foi encerrada.",
    already_answered:
      "Você já respondeu esta pesquisa. Obrigado pela participação!",
    invalid_product:
      "Esta página não está disponível ou o link é inválido. Verifique o endereço ou solicite um novo link à Atlântico.",
  };
  return map[reason] || "Não foi possível abrir a pesquisa.";
};

const questionBodyByProduct = {
  essencial:
    "o quanto foi fácil para sua organização lidar com as rotinas e entregas relacionadas à contabilidade, obrigações fiscais e tributárias, e gestão de pessoas (folha, admissões, férias, etc.) com o Atlântico Essencial?",
  saveRH:
    "o quanto foi fácil para sua organização gerenciar as rotinas de recursos humanos — como admissões de novos colaboradores, gestão de ponto, organização e cronograma de férias, comunicação com colaboradores e uso das ferramentas — utilizando o Save RH?",
  saveFinanc:
    "o quanto foi fácil para sua organização lidar com as rotinas financeiras — como contas a pagar, contas a receber, conciliações, controle de fluxo de caixa e relatórios — com o suporte do Save BPO Financeiro?",
} as const;

type CesProductCode = keyof typeof questionBodyByProduct;

const formatPeriodMonthLabel = (period: string): string | null => {
  const trimmed = period.trim();
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return null;
  const base = dayjs(`${trimmed}-01`, "YYYY-MM-DD", true);
  if (!base.isValid()) return null;
  return base.locale("pt-br").format("MMMM [de] YYYY");
};

const buildQuestionText = (
  code: CesProductCode,
  periodMonthLabel: string | null,
) => {
  const body = questionBodyByProduct[code];
  const lead = "Em uma escala de 1 a 5";
  if (periodMonthLabel) {
    return `${lead}, no mês de ${periodMonthLabel}, ${body}`;
  }
  return `${lead}, ${body}`;
};

const isCesProductCode = (value: string): value is CesProductCode =>
  Object.prototype.hasOwnProperty.call(questionBodyByProduct, value);

const ratingOptions: { value: number; label: string }[] = [
  { value: 1, label: "Muito\u00A0difícil" },
  { value: 2, label: "Difícil" },
  { value: 3, label: "Neutro" },
  { value: 4, label: "Fácil" },
  { value: 5, label: "Muito\u00A0fácil" },
];

const CesProductsSurvey = () => {
  const { surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("user_id") || "";
  const clientId = searchParams.get("client_id") || "";
  const product = searchParams.get("product") || "";

  const querySuffix = useMemo(() => {
    const q = new URLSearchParams({
      user_id: userId,
      client_id: clientId,
      product,
    });
    return q.toString();
  }, [userId, clientId, product]);

  const [phase, setPhase] = useState<
    "loading" | "blocked" | "ready" | "done" | "submit_error"
  >("loading");
  const [blockReason, setBlockReason] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [surveyPeriod, setSurveyPeriod] = useState<string | null>(null);
  const ratingFromUrlApplied = useRef(false);

  useEffect(() => {
    if (ratingFromUrlApplied.current) return;
    const raw = searchParams.get("rating");
    if (raw == null || raw === "") return;
    const n = parseInt(raw, 10);
    if (n >= 1 && n <= 5) {
      setRating(n);
      ratingFromUrlApplied.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!surveyId) {
        setBlockReason(reasonMessage("invalid_params"));
        setPhase("blocked");
        return;
      }
      if (!product.trim()) {
        setBlockReason(reasonMessage("missing_params"));
        setPhase("blocked");
        return;
      }
      if (!isCesProductCode(product)) {
        setBlockReason(reasonMessage("invalid_product"));
        setPhase("blocked");
        return;
      }

      try {
        const res = await fetchData(
          `/surveys/ces-products/${surveyId}/validate?${querySuffix}`,
          { method: "GET" },
        );
        if (cancelled) return;
        if (res?.ok) {
          if (typeof res.period === "string" && res.period.trim()) {
            setSurveyPeriod(res.period.trim());
          } else {
            setSurveyPeriod(null);
          }
          setPhase("ready");
        } else {
          setBlockReason(reasonMessage(res?.reason || ""));
          setPhase("blocked");
        }
      } catch {
        if (!cancelled) {
          setBlockReason(
            "Não foi possível validar o link. Tente novamente mais tarde.",
          );
          setPhase("blocked");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [surveyId, querySuffix, product]);

  const questionText = useMemo(() => {
    if (!isCesProductCode(product)) return "";
    const monthLabel = surveyPeriod
      ? formatPeriodMonthLabel(surveyPeriod)
      : null;
    return buildQuestionText(product, monthLabel);
  }, [product, surveyPeriod]);

  const handleSubmit = async () => {
    if (rating === null || !surveyId) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/surveys/ces-products/${surveyId}/score?${querySuffix}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rating }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : Array.isArray(data?.message)
            ? data.message[0]
            : "Não foi possível enviar sua resposta.";
        const friendly: Record<string, string> = {
          client_not_allowed_for_user:
            "Seu usuário não está autorizado a responder por esta organização.",
          user_not_found: "Usuário não encontrado.",
          client_not_found: "Organização não encontrada.",
          survey_closed: "Esta pesquisa já foi encerrada.",
          already_answered: "Você já respondeu esta pesquisa.",
          invalid_product: reasonMessage("invalid_product"),
        };
        setSubmitError(friendly[msg] || msg);
        setPhase("submit_error");
        setSubmitting(false);
        return;
      }
      setPhase("done");
    } catch {
      setSubmitError("Não foi possível enviar sua resposta.");
      setPhase("submit_error");
    }
    setSubmitting(false);
  };

  if (phase === "loading") {
    return <LinearProgress />;
  }

  if (phase === "blocked") {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" color="error">
          Ops!
        </Typography>
        <Typography variant="body1">{blockReason}</Typography>
      </Stack>
    );
  }

  if (phase === "done") {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" color="primary.main">
          Agradecemos sua participação!
        </Typography>
        <Typography variant="body1">
          Sua opinião foi registrada com sucesso.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" spacing={3} sx={{ width: "100%" }}>
      <Typography variant="h5">{questionText}</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 1.5 },
          width: "100%",
        }}
      >
        {ratingOptions.map(({ value: num, label }) => {
          const selected = rating === num;
          return (
            <Button
              key={num}
              variant="outlined"
              onClick={() => setRating(num)}
              sx={{
                flex: "0 0 auto",
                width: { xs: 98, sm: 108 },
                minHeight: 88,
                borderRadius: 2,
                borderWidth: "2px",
                borderColor: selected ? "primary.main" : "divider",
                backgroundColor: "background.default",
                color: "text.primary",
                textTransform: "none",
                p: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                boxShadow: selected ? 4 : 0,
                transform: selected ? "translateY(-2px)" : "none",
                transition: (theme) =>
                  theme.transitions.create(
                    [
                      "transform",
                      "box-shadow",
                      "border-color",
                      "background-color",
                    ],
                    { duration: theme.transitions.duration.short },
                  ),
                "&:hover": {
                  borderWidth: "2px",
                  borderColor: selected ? "primary.main" : "primary.light",
                  backgroundColor: "background.default",
                  boxShadow: selected ? 6 : 1,
                },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  backgroundColor: colorSwitch(num),
                  color: "common.white",
                  boxShadow: selected ? 2 : 0,
                }}
              >
                {num}
              </Box>
              <Typography
                variant="caption"
                component="div"
                sx={{
                  textAlign: "center",
                  lineHeight: 1.25,
                  px: 0.25,
                  fontWeight: selected ? 700 : 400,
                  color: selected ? "primary.main" : "text.secondary",
                  whiteSpace: "normal",
                }}
              >
                {label}
              </Typography>
            </Button>
          );
        })}
      </Box>
      {phase === "submit_error" && submitError && (
        <Typography color="error" variant="body2">
          {submitError}
        </Typography>
      )}
      <Button
        variant="contained"
        disabled={rating === null || submitting}
        onClick={handleSubmit}
      >
        Enviar avaliação
      </Button>
    </Stack>
  );
};

export default CesProductsSurvey;
